/**
 * Documents Required Banner
 * Compact "at-a-glance" checklist at the top of the Application Detail page.
 * Shows 4 mandatory docs (passport / CV / qualification / English test) with
 * green check when on file or red dot when missing — so a counsellor can
 * scan in 2 seconds which application is blocked on what document.
 *
 * Backend: GET /api/admin/student/{student_id}/documents
 */
import { useEffect, useState, useCallback } from 'react';
import { Check, X, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';

// Each requirement maps to one or more document_type values from the
// student_documents collection (we accept synonyms used across legacy + new
// upload paths so a doc uploaded as "transcript" still counts as
// "Academic Qualification").
const REQUIREMENTS = [
  { key: 'passport', label: 'Passport', matches: ['passport'] },
  { key: 'cv', label: 'CV / Resume', matches: ['cv', 'resume', 'curriculum vitae', 'curriculum_vitae'] },
  {
    key: 'qualification',
    label: 'Academic Qualification',
    matches: [
      'qualification', 'academic_transcript', 'transcript', 'degree',
      'degree_certificate', 'academic', 'certificate', 'bachelor', 'bachelors',
      'masters', 'diploma', 'matric', 'intermediate',
    ],
  },
  {
    key: 'english_test',
    label: 'English Test',
    matches: ['english_test', 'english', 'ielts', 'toefl', 'pte', 'duolingo', 'ellt'],
  },
];

export default function DocumentsRequiredBanner({ studentId }) {
  const [docs, setDocs] = useState(null); // null = loading
  const [error, setError] = useState(null);

  const fetchDocs = useCallback(async () => {
    if (!studentId) return;
    try {
      const { data } = await api.get(`/admin/student/${studentId}/documents`);
      setDocs(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load documents');
      setDocs([]);
    }
  }, [studentId]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  if (docs === null) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-500 py-2">
        <Loader2 className="w-3 h-3 animate-spin" />
        Checking documents…
      </div>
    );
  }

  const uploadedTypes = new Set(docs.map(d => (d.document_type || '').toLowerCase()));
  const checklist = REQUIREMENTS.map(req => ({
    ...req,
    present: req.matches.some(m => uploadedTypes.has(m)),
  }));

  const completeCount = checklist.filter(r => r.present).length;
  const allComplete = completeCount === REQUIREMENTS.length;

  return (
    <div
      className={`rounded-xl px-4 py-3 mb-4 border ${
        allComplete
          ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800'
          : 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800'
      }`}
      data-testid="documents-required-banner"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-semibold ${allComplete ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-800 dark:text-amber-300'}`}>
            Documents Required
          </span>
          <Badge
            variant="outline"
            className={`text-xs ${allComplete ? 'border-emerald-400 text-emerald-700' : 'border-amber-400 text-amber-700'}`}
            data-testid="docs-required-progress"
          >
            {completeCount}/{REQUIREMENTS.length} on file
          </Badge>
          {allComplete && (
            <span className="text-xs text-emerald-700 dark:text-emerald-300 font-medium">All mandatory documents uploaded ✓</span>
          )}
        </div>
        {error && <span className="text-xs text-rose-600">{error}</span>}
      </div>

      <div className="mt-2 flex flex-wrap gap-2">
        {checklist.map((req) => (
          <div
            key={req.key}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${
              req.present
                ? 'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-800/40 dark:text-emerald-200 dark:border-emerald-600'
                : 'bg-rose-50 text-rose-700 border-rose-300 dark:bg-rose-900/30 dark:text-rose-200 dark:border-rose-600'
            }`}
            data-testid={`docs-required-${req.key}-${req.present ? 'ok' : 'missing'}`}
            title={req.present ? `${req.label} on file` : `${req.label} missing — ask the student to upload`}
          >
            {req.present ? (
              <Check className="w-3.5 h-3.5" />
            ) : (
              <span className="w-2 h-2 rounded-full bg-rose-500 inline-block" aria-hidden="true" />
            )}
            <span>{req.label}</span>
            {!req.present && <X className="w-3 h-3 opacity-60" />}
          </div>
        ))}
      </div>
    </div>
  );
}
